import { z } from 'zod';

export const POST_TRIP_TYRE_CONDITIONS = ['good', 'worn', 'damaged'] as const;
export const POST_TRIP_BODY_DAMAGES = ['none', 'minor', 'major'] as const;
export const POST_TRIP_DEFECT_AREAS = ['tyre', 'body', 'cab', 'engine', 'lights', 'other'] as const;
export const POST_TRIP_DEFECT_SEVERITIES = ['minor', 'major', 'unsafe'] as const;

export const MAX_ODOMETER_KM = 9_999_999;
export const MAX_POST_TRIP_DEFECTS = 20;
export const MAX_POST_TRIP_TEXT_LENGTH = 500;

/**
 * One defect the driver found on return. The phone mints `id`, so the later work order (M9) can point at a defect without re-reading
 * the log and a replayed event cannot raise one twice.
 */
export const postTripDefectSchema = z.object({
  id: z.uuid(),
  area: z.enum(POST_TRIP_DEFECT_AREAS),
  severity: z.enum(POST_TRIP_DEFECT_SEVERITIES),
  description: z.string().trim().min(1, 'Say what is wrong').max(MAX_POST_TRIP_TEXT_LENGTH),
  mediaIds: z.array(z.uuid()).optional(),
});
export type PostTripDefect = z.infer<typeof postTripDefectSchema>;

/**
 * The payload of `post_trip_recorded` (gitaumoses4/chukua#77): the driver's final check on return to the yard. The odometer and the fuel gauge are
 * the readings fuel reconciliation will need, so they are required; the rest is optional. Fuel is the gauge in whole percent, what a driver can
 * read without a dipstick. Tyres are one overall condition, and a damaged tyre is expected to appear as a defect in area `tyre` too.
 */
export const postTripPayloadSchema = z.object({
  odometerKm: z.number().int().min(0).max(MAX_ODOMETER_KM),
  fuelLevelPercent: z.number().int().min(0).max(100),
  tyreCondition: z.enum(POST_TRIP_TYRE_CONDITIONS).optional(),
  bodyDamage: z.enum(POST_TRIP_BODY_DAMAGES).optional(),
  defects: z.array(postTripDefectSchema).max(MAX_POST_TRIP_DEFECTS).optional(),
  note: z.string().max(MAX_POST_TRIP_TEXT_LENGTH).optional(),
  mediaIds: z.array(z.uuid()).optional(),
});
export type PostTripPayload = z.infer<typeof postTripPayloadSchema>;
